import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAppStore } from '@/store/useAppStore';
import { logger } from '@/utils/logger';
import { getEffectiveUserId } from '../../auth/current-user-identity';
import { saveCloudProject } from '../project-api';
import {
    getCloudAutosaveLoadedResources,
    type PendingGlobalCloudField,
} from '../project-remote-update';
import {
    createCloudAutosaveQueue,
    createCloudAutosavePlan,
    runCloudAutosave,
} from '../project-autosave-coordinator';
import type { ProjectScope } from '../project-scope';
import type { ProjectDataAuthority } from '../project-data-readiness.ts';
import { saveScriptBlocks } from '../script-storage-api';
import { useProjectReaderCoordinator } from './useProjectReaderCoordinator';

type AppStateSnapshot = ReturnType<typeof useAppStore.getState>;
type CloudConnectionState = AppStateSnapshot['cloud'];
const PERSONNEL_CLOUD_KEYS = ['personnel', 'castAssignments'] as const;

export type UseProjectSyncCoordinatorInput = {
    cloudEnabled: boolean;
    projectScope: ProjectScope | null;
    shouldSync: boolean;
    user: unknown;
    neededResources: readonly string[];
    hasLoadedCloudProject: boolean;
    authoritativeResourceIds?: readonly string[];
    setCloudState: (updates: Partial<CloudConnectionState>) => void;
};

function appendPendingPersonnelChangesOnAuthorityRestore(
    changedCloudKeys: (keyof AppStateSnapshot)[],
    shouldFlushPendingPersonnelChanges: boolean,
    pendingGlobalFields: ReadonlySet<PendingGlobalCloudField>,
): (keyof AppStateSnapshot)[] {
    if (!shouldFlushPendingPersonnelChanges) {
        return changedCloudKeys;
    }

    const changed = new Set<keyof AppStateSnapshot>(changedCloudKeys);
    for (const key of PERSONNEL_CLOUD_KEYS) {
        if (pendingGlobalFields.has(key)) {
            changed.add(key);
        }
    }
    return Array.from(changed);
}

function shouldFlushPendingPersonnelChangesOnAuthorityRestore(
    currentCloud: CloudConnectionState,
    hadPersonnelWriteAuthority: boolean,
    pendingGlobalFields: ReadonlySet<PendingGlobalCloudField>,
): boolean {
    if (currentCloud.hasLoadedPersonnel !== true || hadPersonnelWriteAuthority) {
        return false;
    }
    return PERSONNEL_CLOUD_KEYS.some(key => pendingGlobalFields.has(key));
}

export function useProjectSyncCoordinator({
    cloudEnabled,
    projectScope,
    shouldSync,
    user,
    neededResources,
    hasLoadedCloudProject,
    authoritativeResourceIds,
    setCloudState,
}: UseProjectSyncCoordinatorInput): ProjectDataAuthority {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const remoteVersion = useRef(0);
    const isSaving = useRef(false);
    const isUpdatingCloudMeta = useRef(false);
    const isApplyingRemoteSnapshot = useRef(false);
    const activeSubscription = useRef<(() => void) | null>(null);
    const mountedRef = useRef(true);
    const loadedResources = useRef<Set<string>>(new Set());
    const preloadedResourceSnapshots = useRef<Set<string>>(new Set());
    const hasInitialLoad = useRef(false);
    const pendingScriptBlocksSave = useRef(false);
    const pendingGlobalCloudFields = useRef<Set<PendingGlobalCloudField>>(new Set());
    const pendingLocalAutosave = useRef(false);
    const hadPersonnelWriteAuthority = useRef(false);
    const shouldSyncRef = useRef(shouldSync);
    const [loadedResourceIds, setLoadedResourceIds] = useState<readonly string[]>([]);

    const handleLoadedResourcesChange = useCallback((resources: ReadonlySet<string>) => {
        const nextResourceIds = Array.from(resources);
        setLoadedResourceIds(currentResourceIds => (
            currentResourceIds.length === nextResourceIds.length
            && currentResourceIds.every((resourceId, index) => resourceId === nextResourceIds[index])
                ? currentResourceIds
                : nextResourceIds
        ));
    }, []);

    const readerRefs = useMemo(() => ({
        activeSubscription,
        loadedResources,
        preloadedResourceSnapshots,
        hasInitialLoad,
        remoteVersion,
        isApplyingRemoteSnapshot,
        pendingGlobalCloudFields,
        pendingLocalAutosave,
        timeoutRef,
        isSaving,
        mountedRef,
    }), []);

    const stateGetter = useCallback(() => useAppStore.getState(), []);

    useEffect(() => {
        shouldSyncRef.current = shouldSync;
    }, [shouldSync]);

    const applyCloudState = useCallback((updates: Partial<CloudConnectionState>) => {
        isUpdatingCloudMeta.current = true;
        setCloudState(updates);
        queueMicrotask(() => {
            isUpdatingCloudMeta.current = false;
        });
    }, [setCloudState]);

    const runQueuedAutosave = useCallback((currentCloud: CloudConnectionState) => runCloudAutosave({
        currentCloud,
        loadedResources: loadedResources.current,
        pendingScriptBlocksSave: pendingScriptBlocksSave.current,
        getActorId: getEffectiveUserId,
        getState: stateGetter,
        saveCloudProject,
        saveScriptBlocks,
        markSynced: () => {
            pendingScriptBlocksSave.current = false;
            pendingLocalAutosave.current = false;

            if (mountedRef.current) {
                applyCloudState({
                    ...stateGetter().cloud,
                    lastSync: new Date().toISOString(),
                });
            }
        },
        warn: logger.warn.bind(logger),
    }), [applyCloudState, stateGetter]);

    useProjectReaderCoordinator({
        cloudEnabled,
        projectScope,
        shouldSync,
        user,
        neededResources,
        onLoadedResourcesChange: handleLoadedResourcesChange,
        hasLoadedCloudProject,
        authoritativeResourceIds,
        stateGetter,
        applyCloudState,
        refs: readerRefs,
    });

    useEffect(() => {
        const autosaveQueue = createCloudAutosaveQueue({
            timeoutRef,
            getRemoteVersion: () => remoteVersion.current,
            hasPendingLocalAutosave: () => pendingLocalAutosave.current,
            isSaving: () => isSaving.current,
            setSaving: value => {
                isSaving.current = value;
            },
            runAutosave: runQueuedAutosave,
            warn: logger.warn.bind(logger),
        });
        const unsubscribe = useAppStore.subscribe((state, prevState) => {
            const currentCloud = state.cloud;
            if (!shouldSyncRef.current || !currentCloud.enabled || !currentCloud.orgId || !currentCloud.projectId) {
                hadPersonnelWriteAuthority.current = false;
                return;
            }
            const hadPersonnelAuthority = hadPersonnelWriteAuthority.current;
            hadPersonnelWriteAuthority.current = currentCloud.hasLoadedPersonnel === true;
            const shouldFlushPendingPersonnelChanges = shouldFlushPendingPersonnelChangesOnAuthorityRestore(
                currentCloud,
                hadPersonnelAuthority,
                pendingGlobalCloudFields.current,
            );

            const autosaveLoadedResources = getCloudAutosaveLoadedResources(
                loadedResources.current,
                currentCloud.hasLoadedCloudProject === true,
                currentCloud.authoritativeResourceIds,
            );
            if (autosaveLoadedResources !== loadedResources.current) {
                loadedResources.current = new Set(autosaveLoadedResources);
                hasInitialLoad.current = true;
            }

            const cloudKeys: (keyof typeof state)[] = [
                'projectMeta',
                'showStructure',
                'rows',
                'assets',
                ...PERSONNEL_CLOUD_KEYS,
            ];
            if (autosaveLoadedResources.has('props')) cloudKeys.push('props');
            if (autosaveLoadedResources.has('sets')) cloudKeys.push('sets');
            if (autosaveLoadedResources.has('sound')) cloudKeys.push('soundClips', 'soundCues', 'micAssignments', 'micSwaps', 'micPackConfig');
            if (autosaveLoadedResources.has('lighting')) cloudKeys.push('lightingCues', 'followSpotCues', 'followSpots');
            if (autosaveLoadedResources.has('promptBook')) cloudKeys.push('cueAnchors', 'customCues');
            if (autosaveLoadedResources.has('fly')) cloudKeys.push('flyCues');
            if (autosaveLoadedResources.has('projections')) cloudKeys.push('projectionCues');

            const changedCloudKeys = appendPendingPersonnelChangesOnAuthorityRestore(
                cloudKeys.filter(key => state[key] !== prevState[key]),
                shouldFlushPendingPersonnelChanges,
                pendingGlobalCloudFields.current,
            );
            const autosavePlan = createCloudAutosavePlan({
                changedKeys: changedCloudKeys,
                currentBlocks: state.showStructure.scriptBlocks,
                previousBlocks: prevState.showStructure.scriptBlocks,
                hasInitialLoad: hasInitialLoad.current,
                isApplyingRemoteSnapshot: isApplyingRemoteSnapshot.current && !shouldFlushPendingPersonnelChanges,
                isUpdatingCloudMetadata: isUpdatingCloudMeta.current,
                currentState: state,
                previousState: prevState,
            });

            if (autosavePlan.shouldSaveScriptBlocks) {
                pendingScriptBlocksSave.current = true;
            }
            if (!autosavePlan.shouldQueue) return;

            for (const field of autosavePlan.pendingGlobalFields) {
                pendingGlobalCloudFields.current.add(field);
            }
            pendingLocalAutosave.current = true;
            autosaveQueue.queue(currentCloud, remoteVersion.current);
        });

        return () => {
            unsubscribe();
            autosaveQueue.clear();
        };
    }, [runQueuedAutosave]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    return useMemo(() => ({
        remoteAuthority: cloudEnabled,
        authoritativeResources: loadedResourceIds,
    }), [cloudEnabled, loadedResourceIds]);
}
